import React from "react";
import "./LodgingFacilitySummary.scss";
import { pluralize, stringFormat } from "./shared/helpers";
import { LodgingFacilitySummaryFragment } from "./operations.generated";
import { CheckoutTranslations } from "./translations/CheckoutTranslations";

export interface FacilityValueProps {
    value: string;
    facilityId: number;
}

export interface LodgingFacilitySummaryProps {
    lodging: LodgingFacilitySummaryFragment;
    hideOnMobile?: boolean;
    translations: CheckoutTranslations;
}

export default function LodgingFacilitySummary({
    lodging,
    hideOnMobile,
    translations,
}: LodgingFacilitySummaryProps) {
    const maxPeople = stringFormat(translations.maxPeople, lodging.maxPeople.toString());

    const lodgingAreaText = lodging.lodgingAreaFacility
        ? `${lodging.lodgingAreaFacility.facility.name} ${lodging.lodgingAreaFacility.displayValue}`
        : "";

    const lodgingArea = () => (
        <span
            dangerouslySetInnerHTML={{ __html: lodgingAreaText }}
        />
    );
    
    const useNumericPets = lodging.maxPets !== null;
    const pets = useNumericPets
        ? pluralize(lodging.maxPets, translations.maxPets)
        : lodging.petsAllowed ? translations.petsAllowed : translations.petsNotAllowed;

    const internetFacility = lodging.internetFacility;
    const internetFacilityText = () => internetFacility
        ? internetFacility.facility.name
        : "";    

    const bedroomFacility = lodging.bedroomFacility;
    const bedroomText = () => bedroomFacility
        ? bedroomFacility.displayValueAndName
        : "";

    const bathroomFacility = lodging.bathroomFacility;
    const bathroomText = () =>
        bathroomFacility
            ? bathroomFacility.displayValueAndName
            : "";

    const classNames = ["bwp-lodging-facility-summary"];
    if (hideOnMobile) {
        classNames.push("bwp-only-large-screen");
    }

    return (
        <div className={classNames.join(" ")}>
            <h2>{translations.facilityHeadline}</h2>
            <div className="bwp-lodging-facility-summary__grid">
                <Facility value={maxPeople}>
                    <PeopleIcon />
                </Facility>
                <Facility value={pets}>
                    <PetIcon />
                </Facility>
                {bedroomFacility && (
                    <Facility value={bedroomText()}>
                        <BedroomIcon />
                    </Facility>
                )}
                {lodging.lodgingAreaFacility && (
                    <Facility value={lodgingArea()}>
                        <AreaIcon />
                    </Facility>
                )}
                {lodging.internetFacility && (
                    <Facility value={internetFacilityText()}>
                        <InternetIcon />
                    </Facility>
                )}
                {bathroomFacility && (
                    <Facility value={bathroomText()}>
                        <BathroomIcon />
                    </Facility>
                )}
            </div>
        </div>
    );
}

export interface FacilityProps {
    value: React.ReactNode;
    children: React.ReactNode;
}

function Facility({ value, children }: FacilityProps) {
    if (!value || !children) {
        return null;
    }
    
    return (
        <div className="bwp-lodging-facility-summary__facility">
            <div className="bwp-lodging-facility-summary__facility-icon">{children}</div>
            <div className="bwp-lodging-facility-summary__facility-value">{value}</div>
        </div>
    );
}

function PeopleIcon() {
    return (
        <svg
            width="15"
            height="16"
            viewBox="0 0 15 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7.33333 8C9.64792 8 11.5238 6.20937 11.5238 4C11.5238 1.79063 9.64792 0 7.33333 0C5.01875 0 3.14286 1.79063 3.14286 4C3.14286 6.20937 5.01875 8 7.33333 8ZM10.2667 9H9.71994C8.99316 9.31875 8.18452 9.5 7.33333 9.5C6.48214 9.5 5.67679 9.31875 4.94673 9H4.4C1.97083 9 0 10.8813 0 13.2V14.5C0 15.3281 0.703869 16 1.57143 16H13.0952C13.9628 16 14.6667 15.3281 14.6667 14.5V13.2C14.6667 10.8813 12.6958 9 10.2667 9Z"
                fill="#333333"
            />
        </svg>
    );
}

function PetIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M15.1556 2.13329H13.2L12.9813 1.65629C12.9001 1.47911 12.7752 1.3301 12.6208 1.22596C12.4663 1.12181 12.2883 1.06664 12.1068 1.06662H10.4684L9.63481 0.157286C9.3265 -0.178715 8.80003 0.0592861 8.80003 0.534288V5.52897L12.7111 7.05264V5.86664H13.6889C14.7691 5.86664 15.6445 4.91163 15.6445 3.7333V2.66663C15.6445 2.37196 15.4257 2.13329 15.1556 2.13329ZM11.7334 3.7333C11.4633 3.7333 11.2445 3.49463 11.2445 3.19996C11.2445 2.90529 11.4633 2.66663 11.7334 2.66663C12.0035 2.66663 12.2223 2.90529 12.2223 3.19996C12.2223 3.49463 12.0035 3.7333 11.7334 3.7333ZM2.93334 6.39997C2.39434 6.39997 1.95556 5.9213 1.95556 5.3333C1.95556 4.7443 1.5177 4.26663 0.977781 4.26663C0.437862 4.26663 0 4.7443 0 5.3333C0 6.72197 0.819808 7.89498 1.95556 8.33664V15.4667C1.95556 15.7613 2.17434 16 2.44445 16H4.40001C4.67013 16 4.8889 15.7613 4.8889 15.4667V11.7333H9.77781V15.4667C9.77781 15.7613 9.99658 16 10.2667 16H12.2223C12.4924 16 12.7111 15.7613 12.7111 15.4667V8.18498L8.12933 6.39997H2.93334Z"
                fill="#333333"
            />
        </svg>
    );
}

function BedroomIcon() {
    return (
        <svg
            width="20"
            height="16"
            viewBox="0 0 20 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M5.37778 8C6.72558 8 7.82222 6.50458 7.82222 4.66667C7.82222 2.82875 6.72558 1.33333 5.37778 1.33333C4.02997 1.33333 2.93333 2.82875 2.93333 4.66667C2.93333 6.50458 4.02997 8 5.37778 8ZM16.1333 2.66667H9.28889C9.01878 2.66667 8.8 2.965 8.8 3.33333V9.33333H1.95556V0.666667C1.95556 0.298333 1.73678 0 1.46667 0H0.488889C0.218778 0 0 0.298333 0 0.666667V15.3333C0 15.7017 0.218778 16 0.488889 16H1.46667C1.73678 16 1.95556 15.7017 1.95556 15.3333V13.3333H17.6V15.3333C17.6 15.7017 17.8188 16 18.0889 16H19.0667C19.3368 16 19.5556 15.7017 19.5556 15.3333V7.33333C19.5556 4.75583 18.0235 2.66667 16.1333 2.66667Z"
                fill="#333333"
            />
        </svg>
    );
}

function AreaIcon() {
    return (
        <svg
            width="17"
            height="16"
            viewBox="0 0 17 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M0 5.28571V0.857143C0 0.382143 0.40034 0 0.897959 0H5.53742C5.78435 0 5.98639 0.192857 5.98639 0.428571V1.85714C5.98639 2.09286 5.78435 2.28571 5.53742 2.28571H2.39456V5.28571C2.39456 5.52143 2.19252 5.71429 1.94558 5.71429H0.44898C0.202041 5.71429 0 5.52143 0 5.28571ZM10.7755 0.428571V1.85714C10.7755 2.09286 10.9776 2.28571 11.2245 2.28571H14.3673V5.28571C14.3673 5.52143 14.5694 5.71429 14.8163 5.71429H16.3129C16.5599 5.71429 16.7619 5.52143 16.7619 5.28571V0.857143C16.7619 0.382143 16.3616 0 15.8639 0H11.2245C10.9776 0 10.7755 0.192857 10.7755 0.428571ZM16.3129 10.2857H14.8163C14.5694 10.2857 14.3673 10.4786 14.3673 10.7143V13.7143H11.2245C10.9776 13.7143 10.7755 13.9071 10.7755 14.1429V15.5714C10.7755 15.8071 10.9776 16 11.2245 16H15.8639C16.3616 16 16.7619 15.6179 16.7619 15.1429V10.7143C16.7619 10.4786 16.5599 10.2857 16.3129 10.2857ZM5.98639 15.5714V14.1429C5.98639 13.9071 5.78435 13.7143 5.53742 13.7143H2.39456V10.7143C2.39456 10.4786 2.19252 10.2857 1.94558 10.2857H0.44898C0.202041 10.2857 0 10.4786 0 10.7143V15.1429C0 15.6179 0.40034 16 0.897959 16H5.53742C5.78435 16 5.98639 15.8071 5.98639 15.5714Z"
                fill="#333333"
            />
        </svg>
    );
}

function InternetIcon() {
    return (
        <svg
            width="20"
            height="16"
            viewBox="0 0 20 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M19.8409 4.38857C14.3044 -1.46393 5.69341 -1.46179 0.159036 4.38857C-0.0490891 4.60857 -0.0531516 4.98107 0.148098 5.20929L1.2181 6.4225C1.40997 6.64036 1.71872 6.645 1.9181 6.43607C6.4781 1.66179 13.5212 1.66071 18.0822 6.43607C18.2815 6.645 18.5903 6.64 18.7822 6.4225L19.8522 5.20929C20.0531 4.98107 20.049 4.60857 19.8409 4.38857ZM9.99998 11.4286C8.89529 11.4286 7.99998 12.4518 7.99998 13.7143C7.99998 14.9768 8.89529 16 9.99998 16C11.1047 16 12 14.9768 12 13.7143C12 12.4518 11.1047 11.4286 9.99998 11.4286ZM16.3334 8.44321C12.7315 4.80286 7.26435 4.80679 3.66654 8.44321C3.45091 8.66107 3.44404 9.03929 3.64873 9.27L4.72498 10.4839C4.91248 10.6954 5.21435 10.7096 5.41404 10.5125C8.03748 7.92071 11.9684 7.92643 14.5856 10.5125C14.7853 10.7096 15.0872 10.6957 15.2747 10.4839L16.3509 9.27C16.5559 9.03929 16.5487 8.66072 16.3334 8.44321Z"
                fill="#333333"
            />
        </svg>
    );
}

function BathroomIcon() {
    return (
        <svg
            width="17"
            height="16"
            viewBox="0 0 17 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M12.7567 3.7L7.58214 9.345C7.27539 9.67964 6.77777 9.67964 6.47101 9.345L6.10042 8.94071C5.79366 8.60607 5.79366 8.06321 6.10042 7.72857L6.10402 7.72464C4.98994 6.28857 4.95295 4.19107 5.99304 2.71107C5.60116 2.44143 5.13726 2.28571 4.64095 2.28571C3.23714 2.28571 2.09524 3.53143 2.09524 5.06286V16H0V5.06286C0 2.27107 2.08182 0 4.64095 0C5.84997 0 6.95259 0.507143 7.77923 1.33643C8.95452 0.925357 10.2837 1.17571 11.2714 2.0875L11.275 2.08357C11.5818 1.74893 12.0794 1.74893 12.3861 2.08357L12.7567 2.48786C13.0635 2.8225 13.0635 3.36536 12.7567 3.7ZM12.5714 6.28571C12.5714 6.60132 12.3369 6.85714 12.0476 6.85714C11.7583 6.85714 11.5238 6.60132 11.5238 6.28571C11.5238 5.97011 11.7583 5.71429 12.0476 5.71429C12.3369 5.71429 12.5714 5.97011 12.5714 6.28571ZM13.619 6.28571C13.619 5.97011 13.8536 5.71429 14.1429 5.71429C14.4322 5.71429 14.6667 5.97011 14.6667 6.28571C14.6667 6.60132 14.4322 6.85714 14.1429 6.85714C13.8536 6.85714 13.619 6.60132 13.619 6.28571ZM16.7619 6.28571C16.7619 6.60132 16.5274 6.85714 16.2381 6.85714C15.9488 6.85714 15.7143 6.60132 15.7143 6.28571C15.7143 5.97011 15.9488 5.71429 16.2381 5.71429C16.5274 5.71429 16.7619 5.97011 16.7619 6.28571ZM11.5238 7.42857C11.5238 7.74418 11.2893 8 11 8C10.7107 8 10.4762 7.74418 10.4762 7.42857C10.4762 7.11296 10.7107 6.85714 11 6.85714C11.2893 6.85714 11.5238 7.11296 11.5238 7.42857ZM13.0952 6.85714C13.3845 6.85714 13.619 7.11296 13.619 7.42857C13.619 7.74418 13.3845 8 13.0952 8C12.8059 8 12.5714 7.74418 12.5714 7.42857C12.5714 7.11296 12.8059 6.85714 13.0952 6.85714ZM15.7143 7.42857C15.7143 7.74418 15.4798 8 15.1905 8C14.9012 8 14.6667 7.74418 14.6667 7.42857C14.6667 7.11296 14.9012 6.85714 15.1905 6.85714C15.4798 6.85714 15.7143 7.11296 15.7143 7.42857ZM10.4762 8.57143C10.4762 8.88704 10.2417 9.14286 9.95238 9.14286C9.66307 9.14286 9.42857 8.88704 9.42857 8.57143C9.42857 8.25582 9.66307 8 9.95238 8C10.2417 8 10.4762 8.25582 10.4762 8.57143ZM11.5238 8.57143C11.5238 8.25582 11.7583 8 12.0476 8C12.3369 8 12.5714 8.25582 12.5714 8.57143C12.5714 8.88704 12.3369 9.14286 12.0476 9.14286C11.7583 9.14286 11.5238 8.88704 11.5238 8.57143ZM14.6667 8.57143C14.6667 8.88704 14.4322 9.14286 14.1429 9.14286C13.8536 9.14286 13.619 8.88704 13.619 8.57143C13.619 8.25582 13.8536 8 14.1429 8C14.4322 8 14.6667 8.25582 14.6667 8.57143ZM10.4762 9.71429C10.4762 9.39868 10.7107 9.14286 11 9.14286C11.2893 9.14286 11.5238 9.39868 11.5238 9.71429C11.5238 10.0299 11.2893 10.2857 11 10.2857C10.7107 10.2857 10.4762 10.0299 10.4762 9.71429ZM13.619 9.71429C13.619 10.0299 13.3845 10.2857 13.0952 10.2857C12.8059 10.2857 12.5714 10.0299 12.5714 9.71429C12.5714 9.39868 12.8059 9.14286 13.0952 9.14286C13.3845 9.14286 13.619 9.39868 13.619 9.71429ZM10.4762 10.8571C10.4762 11.1728 10.2417 11.4286 9.95238 11.4286C9.66307 11.4286 9.42857 11.1728 9.42857 10.8571C9.42857 10.5415 9.66307 10.2857 9.95238 10.2857C10.2417 10.2857 10.4762 10.5415 10.4762 10.8571ZM12.5714 10.8571C12.5714 11.1728 12.3369 11.4286 12.0476 11.4286C11.7583 11.4286 11.5238 11.1728 11.5238 10.8571C11.5238 10.5415 11.7583 10.2857 12.0476 10.2857C12.3369 10.2857 12.5714 10.5415 12.5714 10.8571ZM11.5238 12C11.5238 12.3156 11.2893 12.5714 11 12.5714C10.7107 12.5714 10.4762 12.3156 10.4762 12C10.4762 11.6844 10.7107 11.4286 11 11.4286C11.2893 11.4286 11.5238 11.6844 11.5238 12ZM10.4762 13.1429C10.4762 13.4585 10.2417 13.7143 9.95238 13.7143C9.66307 13.7143 9.42857 13.4585 9.42857 13.1429C9.42857 12.8273 9.66307 12.5714 9.95238 12.5714C10.2417 12.5714 10.4762 12.8273 10.4762 13.1429Z"
                fill="#333333"
            />
        </svg>
    );
}
